import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../../../utils/auth'
import { useMutation } from '@apollo/client'
import {
  ChatAiThreadMessage,
  ChatAiThreadMessageAuthorEnum,
  ChatAiThreadMessageUserReactionEnum
} from '../../../../__generated__/graphql'
import {
  TOGGLE_SAVE_CHAT_AI_MESSAGE_MUTATION,
  CHAT_AI_MESSAGE_USER_REACTION_MUTATION,
  SYNTHESIZE_CHAT_AI_MESSAGE_MUTATION
} from '../../../../gql'
import Image from 'next/image'
import { ReactSVG } from 'react-svg'
import classNames from 'classnames'
import styles from '../_chatAssistPage.module.scss'
import { t } from '../../../../utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faStop, faVolumeHigh } from '@fortawesome/free-solid-svg-icons'
import useErrorHandling from '../../../../utils/hooks/useErrorHandling'
import {
  playChatAiAudio,
  stopChatAiPlayback,
  subscribeToChatAiPlayback
} from '../../../../utils/chatAiVoice'

const chatAssistIcon = require('@public/images/lab/chat_assist.svg')

const ChatMessage: React.FC<{
  threadId: number
  chatAiThreadMessage: ChatAiThreadMessage
  scrollToMessage?: string
  isReferenced: boolean
  isFocused: boolean
  scrollAlign?: ScrollLogicalPosition
  textToSpeechEnabled?: boolean
}> = ({
  threadId,
  chatAiThreadMessage,
  isFocused,
  isReferenced,
  scrollAlign = 'nearest',
  textToSpeechEnabled = false
}) => {
  const { currentUser } = useAuth()
  const errorHandling = useErrorHandling()
  const focusedMessageRef = useRef<HTMLDivElement>(null)
  const audioRef = useRef<HTMLAudioElement>(null)
  const [message, setMessage] =
    useState<ChatAiThreadMessage>(chatAiThreadMessage)
  const [isSynthesizing, setIsSynthesizing] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)

  useEffect(() => {
    return subscribeToChatAiPlayback((playingMessageId) => {
      setIsPlaying(playingMessageId === message.id)
    })
  }, [message.id])

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
      }
    }
  }, [])

  const [toggleSaveMessage, { loading }] = useMutation(
    TOGGLE_SAVE_CHAT_AI_MESSAGE_MUTATION,
    {
      variables: {
        threadId,
        messageId: message.id
      },
      onCompleted: (data) => {
        setMessage(data.toggleSaveChatAiMessage.chatAiThreadMessage)
      }
    }
  )

  const [messageFeedback] = useMutation(
    CHAT_AI_MESSAGE_USER_REACTION_MUTATION,
    {
      onCompleted: (data) => {
        setMessage(data.chatAiMessageUserReaction.chatAiThreadMessage)
      }
    }
  )

  const [synthesizeMessage] = useMutation(SYNTHESIZE_CHAT_AI_MESSAGE_MUTATION, {
    onError: errorHandling
  })

  const playAudio = async (audioUrl?: string | null) => {
    const audio = audioRef.current
    if (!audio || !audioUrl) return

    if (audio.src !== audioUrl) {
      audio.src = audioUrl
    }

    await playChatAiAudio(message.id, audio)
  }

  const toggleMessagePlayback = async () => {
    if (!textToSpeechEnabled || isSynthesizing) return

    if (isPlaying) {
      stopChatAiPlayback()
      return
    }

    if (message.audioUrl) {
      try {
        await playAudio(message.audioUrl)
      } catch {
        // Autoplay/user-gesture failures are ignored; button stays idle.
      }
      return
    }

    setIsSynthesizing(true)

    try {
      const response = await synthesizeMessage({
        variables: {
          threadId,
          messageId: message.id
        }
      })

      const updatedMessage =
        response.data?.synthesizeChatAiMessage?.chatAiThreadMessage

      if (!updatedMessage?.audioUrl) return

      setMessage(updatedMessage)

      await new Promise<void>((resolve) => {
        requestAnimationFrame(async () => {
          try {
            await playAudio(updatedMessage.audioUrl)
          } catch {
            // Autoplay/user-gesture failures are ignored; button stays idle.
          } finally {
            resolve()
          }
        })
      })
    } finally {
      setIsSynthesizing(false)
    }
  }

  const sendMessageFeedback = (feedback_type) => {
    messageFeedback({
      variables: {
        threadId,
        messageId: message.id,
        userReaction: feedback_type
      }
    })
  }
  const isAssistMessage =
    message.author === ChatAiThreadMessageAuthorEnum.Assistant

  useEffect(() => {
    if (isFocused && focusedMessageRef?.current) {
      focusedMessageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: scrollAlign
      })
      focusedMessageRef.current.focus()
    }
  }, [isFocused, scrollAlign])

  const cleanChatAiResponse = () => {
    return { __html: message.content }
  }

  return (
    <div data-message-id={message.id}>
      <div
        ref={focusedMessageRef}
        className={classNames(styles.chatMessage, {
          [styles.highlighted]: isReferenced
        })}
      >
        <div className={styles.messageSidebar}>
          {isAssistMessage ? (
            <>
              <Image
                src={chatAssistIcon}
                alt={t('Chat assist')}
                className={styles.authorIcon}
                width={49}
                height={49}
              />
              <button onClick={() => toggleSaveMessage()} disabled={loading}>
                <ReactSVG
                  wrapper="span"
                  src="../../../images/lab/save_chat_icon.svg"
                  className={classNames(styles.saveMessageIcon, {
                    [styles.saveMessageIconLoading]: loading,
                    [styles.saveMessageIconSaved]: message.saved
                  })}
                />
              </button>

              <div className={styles.verticalLine} />
            </>
          ) : (
            <Image
              className={styles.authorIcon}
              src={currentUser.picture}
              width={49}
              height={49}
              alt={`${currentUser.firstName} ${currentUser.lastName}`}
            />
          )}
        </div>
        {isAssistMessage ? (
          <article className={styles.chatMessageContent}>
            <div className={styles.assistantMessageHeader}>
              <h3>{t('LAB+ Coach')}</h3>

              {textToSpeechEnabled && (
                <button
                  type="button"
                  className={classNames(styles.voiceButton, {
                    [styles.voiceButtonActive]: isPlaying
                  })}
                  onClick={toggleMessagePlayback}
                  disabled={isSynthesizing}
                  aria-label={
                    isPlaying
                      ? t('Stop reading aloud')
                      : t('Read response aloud')
                  }
                  title={
                    isPlaying
                      ? t('Stop reading aloud')
                      : t('Read response aloud')
                  }
                >
                  <FontAwesomeIcon
                    color={isPlaying ? '#b700ba' : '#7479dc'}
                    icon={isPlaying ? faStop : faVolumeHigh}
                    size="sm"
                  />
                </button>
              )}
            </div>

            <div dangerouslySetInnerHTML={cleanChatAiResponse()} />

            <audio
              ref={audioRef}
              preload="none"
              className={styles.messageAudio}
            />
          </article>
        ) : (
          <article>
            <h3>{t('You')}</h3>

            <div className={styles.userMessageContent}>{message.content}</div>
          </article>
        )}
      </div>
      <div className={styles.messageFeedback}>
        {isAssistMessage && (
          <>
            <span>Was this response helpful? </span>
            <button
              onClick={() =>
                sendMessageFeedback(ChatAiThreadMessageUserReactionEnum.Like)
              }
            >
              <ReactSVG
                wrapper="span"
                src="../../../images/lab/message_feedback_like.svg"
                className={classNames({
                  [styles.feedbackChosen]:
                    message.userReaction ==
                    ChatAiThreadMessageUserReactionEnum.Like
                })}
              />
            </button>
            <button
              onClick={() =>
                sendMessageFeedback(ChatAiThreadMessageUserReactionEnum.Dislike)
              }
            >
              <ReactSVG
                wrapper="span"
                src="../../../images/lab/message_feedback_dislike.svg"
                className={classNames({
                  [styles.feedbackChosen]:
                    message.userReaction ==
                    ChatAiThreadMessageUserReactionEnum.Dislike
                })}
              />
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default ChatMessage
